"use client"

import { useState } from "react"
import type { Project, ProjectFormFieldFileValue } from "@/schema/project"
import type { ProjectMilestoneFormField } from "@/schema/project-template"
import { Button } from "@ui/shad"
import { Check, Copy, Paperclip, Pencil, X } from "lucide-react"

import { useUpsertFormFieldResponses } from "@/hooks/api/projects/mutations"
import { formatCurrencyDisplay } from "@/components/custom-fields/number-format"

import { MilestoneFormFieldsInput } from "./milestone-form-fields-input"

function extractFileValue(
  value: string | number | boolean | ProjectFormFieldFileValue | null | undefined
): ProjectFormFieldFileValue | null {
  if (value && typeof value === "object" && "storage_file_id" in value) return value as ProjectFormFieldFileValue
  return null
}

function visitedMilestonesWithFormFields(project: Project) {
  const visitedIds = new Set(project.visited_project_milestone_ids ?? [])
  if (project.project_milestone_id) visitedIds.add(project.project_milestone_id)

  return (project.project_template?.project_milestones ?? []).filter(
    (milestone) => (milestone.project_milestone_form_fields?.length ?? 0) > 0 && visitedIds.has(milestone.id)
  )
}

export function hasVisitedMilestoneFormFields(project: Project | undefined | null): boolean {
  return (
    !!project &&
    (visitedMilestonesWithFormFields(project).length > 0 || (project.archived_form_responses?.length ?? 0) > 0)
  )
}

interface ProjectFormResponsesProps {
  project: Project
}

export function ProjectFormResponses({ project }: ProjectFormResponsesProps) {
  const responses = project.project_form_field_responses ?? []

  const upsertResponses = useUpsertFormFieldResponses()
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<Record<string, string | number | boolean | null>>({})

  const milestonesWithFields = visitedMilestonesWithFormFields(project)

  const archivedResponses = project.archived_form_responses ?? []

  if (milestonesWithFields.length === 0 && archivedResponses.length === 0) return null

  const responseMap = new Map(responses.map((r) => [r.project_milestone_form_field_id, r]))

  const handleEdit = (milestoneId: string, fields: ProjectMilestoneFormField[]) => {
    const initial: Record<string, string | number | boolean | null> = {}
    for (const field of fields) {
      const response = responseMap.get(field.id)
      if (!response) continue
      if (field.field_type === "file_attachment") {
        initial[field.id] = extractFileValue(response.value)?.storage_file_id ?? null
      } else {
        initial[field.id] = (response.value as string | number | boolean | null | undefined) ?? null
      }
    }
    setEditValues(initial)
    setEditingMilestoneId(milestoneId)
  }

  const handleSave = () => {
    upsertResponses.mutate(
      { projectId: project.id, responses: editValues },
      { onSuccess: () => setEditingMilestoneId(null) }
    )
  }

  return (
    <div className="px-4 py-4">
      <h3 className="mb-3 text-sm font-medium">Form responses</h3>
      <div className="space-y-4">
        {milestonesWithFields.map((milestone) => {
          const fields = milestone.project_milestone_form_fields ?? []
          const isEditing = editingMilestoneId === milestone.id

          return (
            <div key={milestone.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: milestone.color }} />
                  <span className="text-muted-foreground text-xs font-medium">{milestone.title}</span>
                </div>
                {isEditing ? (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="iconXs"
                      aria-label="Cancel editing form responses"
                      onClick={() => setEditingMilestoneId(null)}
                      disabled={upsertResponses.isPending}
                    >
                      <X />
                    </Button>
                    <Button
                      aria-label="Save form responses"
                      variant="ghost"
                      size="iconXs"
                      onClick={handleSave}
                      disabled={upsertResponses.isPending}
                    >
                      <Check />
                    </Button>
                  </div>
                ) : (
                  <Button
                    aria-label="Edit form responses"
                    variant="ghost"
                    size="iconXs"
                    onClick={() => handleEdit(milestone.id, fields)}
                  >
                    <Pencil />
                  </Button>
                )}
              </div>

              {isEditing ? (
                <MilestoneFormFieldsInput
                  fields={fields.map((field) => ({
                    ...field,
                    current_file:
                      field.field_type === "file_attachment"
                        ? extractFileValue(responseMap.get(field.id)?.value)
                        : null,
                  }))}
                  values={editValues}
                  onChange={setEditValues}
                  disabled={upsertResponses.isPending}
                  householdRecordScope={{ householdId: project.household?.id ?? null }}
                  fileAttachmentScope={{
                    householdId: project.household?.id ?? null,
                    projectTemplateId: project.project_template?.id ?? null,
                    hasStorageFolder: !!project.household?.storage_folder_id,
                  }}
                />
              ) : (
                <div className="space-y-3">
                  {fields.map((field) => {
                    const response = responseMap.get(field.id)
                    if (field.field_type === "file_attachment") {
                      const fileValue = extractFileValue(response?.value)
                      const fileHref = fileValue?.web_url ?? fileValue?.download_url ?? null
                      return (
                        <div key={field.id} className="group/row space-y-1.5 text-sm">
                          <div className="text-muted-foreground text-xs leading-snug font-medium">{field.label}</div>
                          {fileValue ? (
                            fileHref ? (
                              <a
                                href={fileHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:bg-muted flex h-8 min-w-0 items-center gap-1.5 rounded-md border px-2 text-xs hover:underline"
                              >
                                <Paperclip className="size-3 shrink-0" />
                                <span className="min-w-0 truncate">{fileValue.file_name}</span>
                              </a>
                            ) : (
                              <span className="flex h-8 min-w-0 items-center gap-1.5 rounded-md border px-2 text-xs">
                                <Paperclip className="size-3 shrink-0" />
                                <span className="min-w-0 truncate">{fileValue.file_name}</span>
                              </span>
                            )
                          ) : (
                            <span className="text-muted-foreground italic">Not set</span>
                          )}
                        </div>
                      )
                    }
                    const displayValue =
                      field.field_type === "household_record"
                        ? (response?.display_value ?? "")
                        : formatDisplayValue(field, response?.value)
                    return (
                      <div key={field.id} className="group/row space-y-1.5 text-sm">
                        <div className="text-muted-foreground text-xs leading-snug font-medium">{field.label}</div>
                        <div className="flex min-w-0 items-start gap-1.5">
                          <div
                            className={
                              displayValue
                                ? "min-w-0 flex-1 leading-relaxed break-words whitespace-pre-wrap"
                                : "text-muted-foreground min-w-0 flex-1 italic"
                            }
                          >
                            {displayValue || "Not set"}
                          </div>
                          {displayValue && <CopyValueButton value={displayValue} />}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
        {archivedResponses.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-muted-foreground text-xs font-medium">Removed inputs</h4>
            {archivedResponses.map((response) => {
              const fileHref = response.file?.web_url ?? response.file?.download_url
              return (
                <div key={response.id} className="group/row space-y-1.5 text-sm">
                  <div className="text-muted-foreground text-xs font-medium">
                    {response.milestone_title} · {response.label}
                  </div>
                  <div className="flex min-w-0 items-start gap-1.5">
                    {fileHref ? (
                      <a
                        href={fileHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex min-w-0 flex-1 items-center gap-1.5 hover:underline"
                      >
                        <Paperclip className="size-3 shrink-0" />
                        {response.file?.file_name}
                      </a>
                    ) : (
                      <div className="min-w-0 flex-1 leading-relaxed break-words whitespace-pre-wrap">
                        {response.display_value ?? <span className="text-muted-foreground italic">Not set</span>}
                      </div>
                    )}
                    {response.display_value && <CopyValueButton value={response.display_value} />}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function CopyValueButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <Button
      variant="ghost"
      size="iconXs"
      className="opacity-0 group-hover/row:opacity-100"
      onClick={() => {
        navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
    >
      {copied ? <Check className="text-green-accent" /> : <Copy />}
    </Button>
  )
}

function formatDisplayValue(
  field: ProjectMilestoneFormField,
  value: string | number | boolean | ProjectFormFieldFileValue | null | undefined
): string {
  if (value == null) return ""
  if (typeof value === "object") return ""
  if (field.field_type === "boolean") return value === true || value === "true" ? "Yes" : "No"
  if (field.field_type === "currency") return formatCurrencyDisplay(value as string | number)
  return String(value)
}
